import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';

const RecommendPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const referrerEmail = searchParams.get('from') || '';
  const referrerName = searchParams.get('name') || '';
  
  const [formData, setFormData] = useState({
    recipientFirstName: '',
    recipientLastName: '',
    recipientEmail: '',
    schoolName: '',
    message: `Bonjour,

Je me permets de vous recommander DailyGlobe School, une plateforme pédagogique innovante pour les écoles de journalisme.

J'ai eu l'occasion de découvrir cette plateforme et je pense qu'elle pourrait vraiment intéresser votre établissement :
- Quiz d'actualité quotidiens générés par IA
- Ateliers d'écriture et d'interview
- Journal public de l'école
- Dashboard enseignant complet avec analytics

Je vous invite à découvrir la plateforme sur https://dailyglobe.school

Bien cordialement,`
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipientEmail || !formData.recipientFirstName) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await api.post('/api/recommendations/send', {
        referrerEmail,
        referrerName,
        recipientFirstName: formData.recipientFirstName,
        recipientLastName: formData.recipientLastName,
        recipientEmail: formData.recipientEmail,
        schoolName: formData.schoolName,
        message: formData.message
      });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Recommandation envoyée avec succès !');
      }
    } catch (error) {
      console.error('Erreur envoi recommandation:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1B4965] to-[#2D5A7B] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-white/95 backdrop-blur">
          <CardHeader className="text-center">
            <div className="text-6xl mb-4">🎉</div>
            <CardTitle className="text-2xl text-[#1B4965]">Merci pour votre recommandation !</CardTitle>
            <CardDescription className="text-base mt-2">
              Votre message a été envoyé à {formData.recipientFirstName} {formData.recipientLastName}.
              Vous recevrez une copie dans votre boîte mail.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#E63946] hover:bg-[#C1121F]"
            >
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1B4965] to-[#2D5A7B] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">DAILYGLOBE SCHOOL</h1>
          <p className="text-[#FFB703] text-lg">Recommandez-nous à un confrère</p>
        </div>
        
        <Card className="bg-white/95 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-xl text-[#1B4965] flex items-center gap-2">
              🤝 Recommander DailyGlobe School
            </CardTitle>
            <CardDescription>
              Partagez la plateforme avec un directeur d'école de journalisme que vous connaissez.
              {referrerName && (
                <span className="block mt-1 text-[#1B4965]">
                  De la part de : <strong>{referrerName}</strong>
                </span>
              )}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Destinataire */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1B4965] border-b pb-2">Destinataire</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="recipientFirstName">Prénom *</Label>
                    <Input
                      id="recipientFirstName"
                      name="recipientFirstName"
                      value={formData.recipientFirstName}
                      onChange={handleChange}
                      placeholder="Prénom"
                      required
                      data-testid="recipient-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="recipientLastName">Nom</Label>
                    <Input
                      id="recipientLastName"
                      name="recipientLastName"
                      value={formData.recipientLastName}
                      onChange={handleChange}
                      placeholder="Nom"
                      data-testid="recipient-lastname"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="recipientEmail">Email *</Label>
                  <Input
                    id="recipientEmail"
                    name="recipientEmail"
                    type="email"
                    value={formData.recipientEmail}
                    onChange={handleChange}
                    placeholder="email@ecole.com"
                    required
                    data-testid="recipient-email"
                  />
                </div>
                
                <div>
                  <Label htmlFor="schoolName">Nom de l'école</Label>
                  <Input
                    id="schoolName"
                    name="schoolName"
                    value={formData.schoolName}
                    onChange={handleChange}
                    placeholder="Ex: ESJ Paris, CFJ, IPJ..."
                    data-testid="school-name"
                  />
                </div>
              </div>
              
              {/* Message */}
              <div className="space-y-2">
                <h3 className="font-semibold text-[#1B4965] border-b pb-2">Message personnalisé</h3>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={12}
                  className="resize-none"
                  data-testid="recommendation-message"
                />
                <p className="text-xs text-gray-500">
                  Vous pouvez personnaliser le message ci-dessus.
                </p>
              </div>
              
              {/* Note */}
              <div className="bg-[#f8f4e3] p-4 rounded-lg text-sm">
                <p className="text-[#856404]">
                  📧 Une copie de ce message vous sera envoyée à <strong>{referrerEmail || 'votre adresse'}</strong>.
                </p>
              </div>
              
              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#E63946] hover:bg-[#C1121F] text-white py-6 text-lg"
                data-testid="send-recommendation-btn"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    📤 Envoyer la recommandation
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center mt-8 text-white/70 text-sm">
          <p>DailyGlobe School - La plateforme pédagogique des écoles de journalisme</p>
        </div>
      </div>
    </div>
  );
};

export default RecommendPage;
